import { useEffect, useRef } from "react";
import * as THREE from "three";

const LOGO_URL = "/HEY_LETS_TRAVEL_LOGO.png";

function createLogoMaterial(texture, { opacity = 1, tint = 0, lift = 0.2 } = {}) {
  return new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    uniforms: {
      uMap: { value: texture },
      uOpacity: { value: opacity },
      uTint: { value: tint },
      uLift: { value: lift },
      uPointer: { value: new THREE.Vector2(0, 0) },
      uGold: { value: new THREE.Color("#d4af37") },
      uCyan: { value: new THREE.Color("#49d8dc") },
    },
    vertexShader: `
      uniform float uLift;
      uniform vec2 uPointer;
      varying vec2 vUv;

      void main() {
        vUv = uv;
        vec3 transformed = position;
        float center = 1.0 - smoothstep(0.05, 0.78, distance(uv, vec2(0.5, 0.5)));
        float topMark = 1.0 - smoothstep(0.0, 0.36, distance(uv, vec2(0.5, 0.68)));
        float depth = max(center * 0.45, topMark * 0.6);

        transformed.z += depth * (uLift + length(uPointer) * 0.26);
        transformed.x += (uv.x - 0.5) * depth * uPointer.x * 0.24;
        transformed.y -= (uv.y - 0.5) * depth * uPointer.y * 0.2;

        gl_Position = projectionMatrix * modelViewMatrix * vec4(transformed, 1.0);
      }
    `,
    fragmentShader: `
      uniform sampler2D uMap;
      uniform float uOpacity;
      uniform float uTint;
      uniform vec3 uGold;
      uniform vec3 uCyan;
      varying vec2 vUv;

      void main() {
        vec4 tex = texture2D(uMap, vUv);
        float brightness = max(max(tex.r, tex.g), tex.b);
        float chroma = brightness - min(min(tex.r, tex.g), tex.b);
        float baseAlpha = smoothstep(0.01, 0.2, tex.a);
        float detailAlpha = max(
          smoothstep(0.02, 0.18, brightness),
          smoothstep(0.03, 0.18, chroma)
        );
        float alpha = baseAlpha * (0.42 + detailAlpha * 0.72);

        if (alpha < 0.01) discard;

        vec3 lifted = min(vec3(1.0), pow(max(tex.rgb, uCyan * 0.1) * 1.92, vec3(0.78)));
        float cyanLift = smoothstep(0.08, 0.34, tex.g + tex.b - tex.r * 0.45);
        float goldSheen = smoothstep(0.0, 0.88, vUv.y) * smoothstep(0.08, 0.62, brightness);
        vec3 glass = lifted + uCyan * (cyanLift * 0.32 + baseAlpha * 0.08) + uGold * goldSheen * 0.1;
        vec3 color = mix(glass, mix(uCyan, uGold, 0.18), uTint);

        gl_FragColor = vec4(color, alpha * uOpacity);
      }
    `,
  });
}

function createGlowTexture() {
  const canvas = document.createElement("canvas");
  canvas.width = 512;
  canvas.height = 512;
  const context = canvas.getContext("2d");
  const gradient = context.createRadialGradient(256, 244, 20, 256, 244, 248);

  gradient.addColorStop(0, "rgba(73,216,220,0.26)");
  gradient.addColorStop(0.34, "rgba(212,175,55,0.12)");
  gradient.addColorStop(0.72, "rgba(73,216,220,0.04)");
  gradient.addColorStop(1, "rgba(0,0,0,0)");
  context.fillStyle = gradient;
  context.fillRect(0, 0, canvas.width, canvas.height);

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

export default function BrandsLogo3D() {
  const shellRef = useRef(null);
  const canvasRef = useRef(null);
  const pointerRef = useRef({ x: 0, y: 0, hovering: false });

  useEffect(() => {
    const shell = shellRef.current;
    const canvas = canvasRef.current;
    if (!shell || !canvas) return undefined;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(32, 1, 0.1, 100);
    camera.position.set(0, 0, 5.3);

    const renderer = new THREE.WebGLRenderer({
      alpha: true,
      antialias: true,
      canvas,
      powerPreference: "high-performance",
    });
    renderer.setClearColor(0x000000, 0);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.8));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.2;

    const textureLoader = new THREE.TextureLoader();
    const logoTexture = textureLoader.load(LOGO_URL);
    logoTexture.colorSpace = THREE.SRGBColorSpace;
    logoTexture.anisotropy = renderer.capabilities.getMaxAnisotropy();

    const glowTexture = createGlowTexture();
    const stage = new THREE.Group();
    const logoGroup = new THREE.Group();
    stage.add(logoGroup);
    scene.add(stage);

    const glow = new THREE.Mesh(
      new THREE.PlaneGeometry(3.9, 3.9),
      new THREE.MeshBasicMaterial({
        map: glowTexture,
        opacity: 0.36,
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      })
    );
    glow.position.set(0.02, 0.02, -0.42);
    logoGroup.add(glow);

    const logoGeometry = new THREE.PlaneGeometry(2.45, 2.5, 74, 74);
    const layers = [
      { x: -0.05, y: 0.03, z: -0.14, opacity: 0.14, tint: 0.4, lift: 0.14, scale: 1.024 },
      { x: -0.025, y: 0.015, z: -0.07, opacity: 0.22, tint: 0.18, lift: 0.18, scale: 1.012 },
      { x: 0, y: 0, z: 0, opacity: 1, tint: 0, lift: 0.25, scale: 1 },
    ];
    const materials = [];

    layers.forEach((layer, index) => {
      const material = createLogoMaterial(logoTexture, layer);
      material.renderOrder = index + 1;
      materials.push(material);

      const mesh = new THREE.Mesh(logoGeometry, material);
      mesh.position.set(layer.x, layer.y, layer.z);
      mesh.scale.setScalar(layer.scale);
      mesh.renderOrder = index + 1;
      logoGroup.add(mesh);
    });

    const orbitGeometry = new THREE.TorusGeometry(1.44, 0.004, 8, 128);
    const orbitMaterial = new THREE.MeshBasicMaterial({
      color: "#d4af37",
      opacity: 0.22,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    const orbit = new THREE.Mesh(orbitGeometry, orbitMaterial);
    orbit.rotation.x = Math.PI * 0.5;
    orbit.rotation.z = -0.2;
    orbit.position.z = -0.18;
    logoGroup.add(orbit);

    const sparkCount = 48;
    const sparkPositions = new Float32Array(sparkCount * 3);
    for (let index = 0; index < sparkCount; index += 1) {
      const angle = (index / sparkCount) * Math.PI * 2;
      const radius = 1.05 + (index % 6) * 0.08;
      sparkPositions[index * 3] = Math.cos(angle) * radius + (Math.random() - 0.5) * 0.12;
      sparkPositions[index * 3 + 1] = Math.sin(angle) * radius * 0.82 + (Math.random() - 0.5) * 0.12;
      sparkPositions[index * 3 + 2] = -0.05 + (Math.random() - 0.5) * 0.32;
    }
    const sparkGeometry = new THREE.BufferGeometry();
    sparkGeometry.setAttribute("position", new THREE.BufferAttribute(sparkPositions, 3));
    const sparks = new THREE.Points(
      sparkGeometry,
      new THREE.PointsMaterial({
        color: "#49d8dc",
        opacity: 0.2,
        size: 0.026,
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      })
    );
    logoGroup.add(sparks);

    let frame = 0;
    let targetScale = 1;
    const targetRotation = { x: 0.02, y: -0.04 };

    const resize = () => {
      const rect = shell.getBoundingClientRect();
      const width = Math.max(1, rect.width);
      const height = Math.max(1, rect.height);

      renderer.setSize(width, height, false);
      camera.aspect = width / height;
      camera.position.z = width < 420 ? 5.55 : 5.1;
      camera.updateProjectionMatrix();
      stage.scale.setScalar(width < 420 ? 0.94 : 1.08);
      stage.position.set(0, 0, 0);
    };

    const onPointerMove = (event) => {
      const rect = shell.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width - 0.5) * 2;
      const y = ((event.clientY - rect.top) / rect.height - 0.5) * 2;

      pointerRef.current.x = Math.max(-1, Math.min(1, x));
      pointerRef.current.y = Math.max(-1, Math.min(1, y));
      pointerRef.current.hovering = true;
      targetRotation.y = -0.04 + pointerRef.current.x * 0.42;
      targetRotation.x = 0.02 - pointerRef.current.y * 0.32;
      targetScale = 1.14;
    };

    const onPointerLeave = () => {
      pointerRef.current.x = 0;
      pointerRef.current.y = 0;
      pointerRef.current.hovering = false;
      targetRotation.y = -0.04;
      targetRotation.x = 0.02;
      targetScale = 1;
    };

    const animate = () => {
      frame = window.requestAnimationFrame(animate);
      const time = performance.now() * 0.001;
      const { x, y, hovering } = pointerRef.current;

      logoGroup.rotation.y += (targetRotation.y - logoGroup.rotation.y) * 0.08;
      logoGroup.rotation.x += (targetRotation.x - logoGroup.rotation.x) * 0.08;
      logoGroup.rotation.z += (x * 0.018 + Math.sin(time * 0.7) * 0.01 - logoGroup.rotation.z) * 0.08;
      logoGroup.position.z += ((hovering ? 0.54 : 0) - logoGroup.position.z) * 0.08;
      logoGroup.position.y += ((hovering ? 0.05 : 0) + Math.sin(time * 1.05) * 0.026 - logoGroup.position.y) * 0.08;
      logoGroup.scale.lerp(new THREE.Vector3(targetScale, targetScale, targetScale), 0.07);

      glow.material.opacity = 0.22 + Math.sin(time * 1.4) * 0.04 + (hovering ? 0.17 : 0);
      orbit.rotation.z = time * 0.24;
      orbit.material.opacity += ((hovering ? 0.44 : 0.2) - orbit.material.opacity) * 0.08;
      sparks.rotation.z = -time * 0.12;
      sparks.rotation.y = x * 0.16;
      sparks.material.opacity += ((hovering ? 0.42 : 0.16) - sparks.material.opacity) * 0.08;

      materials.forEach((material) => {
        material.uniforms.uPointer.value.set(x, y);
      });

      camera.position.x += (x * 0.08 - camera.position.x) * 0.06;
      camera.position.y += (-y * 0.05 - camera.position.y) * 0.06;
      camera.lookAt(0, 0, 0);
      renderer.render(scene, camera);
    };

    resize();
    animate();
    shell.addEventListener("pointermove", onPointerMove);
    shell.addEventListener("pointerleave", onPointerLeave);
    window.addEventListener("resize", resize);

    return () => {
      window.cancelAnimationFrame(frame);
      shell.removeEventListener("pointermove", onPointerMove);
      shell.removeEventListener("pointerleave", onPointerLeave);
      window.removeEventListener("resize", resize);
      logoTexture.dispose();
      glowTexture.dispose();
      logoGeometry.dispose();
      orbitGeometry.dispose();
      sparkGeometry.dispose();
      scene.traverse((object) => {
        if (object.geometry) object.geometry.dispose();
        if (object.material) object.material.dispose();
      });
      renderer.dispose();
    };
  }, []);

  return (
    <div ref={shellRef} className="relative mx-auto h-72 min-h-72 w-full overflow-hidden bg-transparent">
      <canvas
        ref={canvasRef}
        data-brands-logo-3d
        aria-hidden="true"
        className="absolute inset-0 h-full w-full"
      />
    </div>
  );
}
